import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'i18n'
import LegacyRetinaImage from 'components/LegacyRetinaImage'
import Question from './Question'
import PollArrow from 'images/inline/arrow-right-poll.svg'
import ProgressBar from 'steps/shared/ProgressBar'
import { PollProducts } from './PollProducts'
import { usePollColors } from './use-poll-colors'
import { registerOverlayPortal } from '@puckeditor/core'
import PuckPollContext from './PuckPollContext'

const PollItem = ({
  question,
  totalQuestions,
  onAnswerClick,
  onAnswerLinkClick,
  onAnswerDropdownClick,
  backgroundImage,
  handleNextQuestion,
  skipButton,
  nextStepButton,
  onAllQuestionsAnswered,
  socialShareComponent,
  sponsoredType,
  poll,
  isPuck,
  /** Optional slot content rendered at the bottom of the question card (from puck preview). */
  slotContent,
  /** Inline styles for the poll card, set via the puck editor's Card Background / Card Border fields. */
  puckCardInlineStyle,
  puckCardOverlayStyle,
  puckBeforeCardContent,
  puckAfterCardContent,
  puckAfterVoteContent,
  puckProgressBarContent,
  puckProgressBarProps,
}) => {
  const { t } = useTranslation(['poll', 'common'])
  const [pollResults, setPollResults] = useState(null)
  const [resultIsLoading, setResultIsLoading] = useState(false)

  const resultActive = () => pollResults !== null
  const sponsored = sponsoredType === 'sponsored' ? true : false

  const showNextButton = () => {
    return question.index < totalQuestions - 1 && resultActive()
  }

  const localNextQuestionHandler = () => {
    setPollResults(null)
    handleNextQuestion && handleNextQuestion()
  }

  const handleAnswerClick = async ({ questionId, answerId }) => {
    try {
      setResultIsLoading(true)
      let questionData = await onAnswerClick({
        questionId,
        answerId,
      })
      if (question.index === totalQuestions - 1) {
        onAllQuestionsAnswered && onAllQuestionsAnswered()
      }
      setPollResults(questionData)
    } finally {
      setResultIsLoading(false)
    }
  }

  const getTotalQuestionSubmissions = () => {
    let total = 0
    if (pollResults) {
      pollResults.forEach(function (ans) {
        let thisCount = ans.pollAnswerCount || 0
        total = total + thisCount
      })
    }
    return total
  }
  const totalPollSubmissions = getTotalQuestionSubmissions()
  const resultImage = question.textAnsweredImage || null
  const resultText = question.textAnswered || null
  const hasQuestionHeaderImage = question.puckQuestion
    ? !!question.puckHeaderContent
    : !!(question.image && question.image.id)
  const questionHeaderImageURL =
    !question.puckQuestion && question.image?.id
      ? `${process.env.SITE_ASSET_URL}/${question.image.id}`
      : ''

  const products = poll?.productPanel?.products ?? []
  const productsTitle = poll?.productPanel?.headline ?? 'Shop Products'

  const questionAnswersHasImage = question.answers.some(
    answer => answer.type === 'IMAGE',
  )

  const { pollColors } = usePollColors(poll)

  const stepRef = useRef(null)

  useEffect(() => {
    if (!stepRef.current || !isPuck) return

    registerOverlayPortal(stepRef.current, {
      disableDrag: true,
      disableDragOnFocus: true,
    })
  }, [isPuck])

  const puckPollCtxValue = useMemo(() => ({
    poll,
    question,
    products,
    productsTitle,
    onAnswerLinkClick,
    onAnswerDropdownClick,
  }), [poll, question, products, productsTitle, onAnswerLinkClick, onAnswerDropdownClick])

  return (
    <PuckPollContext.Provider value={puckPollCtxValue}>
    <div
      ref={stepRef}
      className={`step-content ${
        backgroundImage ? ' with-shadow' : ' '
      } poll-question  ${
        hasQuestionHeaderImage ? ' question-image' : ' no-question-img'
      }`}
    >
      {puckProgressBarContent}
      {puckBeforeCardContent}
      <div
        className={`poll-item ${sponsored ? 'sponsored' : ''} item-${
          question.index
        } ${
          // if the question has image and no products, then we add extra margin on the question text
          questionAnswersHasImage && !products?.length ? 'has-image' : ''
        } ${hasQuestionHeaderImage ? ' question-image' : ' no-question-img'}`}
        style={puckCardInlineStyle || undefined}
      >
        {puckCardOverlayStyle && <div style={puckCardOverlayStyle} />}
        {question.puckQuestion
          ? question.puckHeaderContent && (
              <div className="poll-item-image" style={{ height: 'auto' }}>
                {question.puckHeaderContent}
              </div>
            )
          : question.image && (
              <div className="poll-item-image">
                <LegacyRetinaImage image={question.image} />
              </div>
            )}
        <div className="poll-item-header">
          <div
            className={`poll-question-header has-no-header-image`}
            style={{ backgroundImage: `${questionHeaderImageURL}` }}
          >
            <ProgressBar
              label={
                pollResults !== null && sponsored
                  ? t('poll:resultsLabel')
                  : t('poll:label')
              }
              position={question.index + 1}
              total={totalQuestions}
              showDots={true}
              showNumber={true}
              {...(puckProgressBarProps || {})}
            />
          </div>
        </div>
        {sponsored ? (
          <h2
            className={`poll-slide-title poll-question-headline`}
            style={question.puckTitleStyle}
            dangerouslySetInnerHTML={{
              __html: resultActive() && resultText ? resultText : question.text,
            }}
          ></h2>
        ) : (
          <h2
            className={`poll-slide-title poll-question-headline`}
            style={question.puckTitleStyle}
            dangerouslySetInnerHTML={{ __html: question.text }}
          ></h2>
        )}

        <div
          className={`poll-view-container ${
            !skipButton ? 'no-skip-button' : ''
          }`}
        >
          <div
            key={`poll-question-slide`}
            className={`poll-view will-fade-transition  ${
              pollResults === null ? `is-active` : `is-inactive`
            } ${resultIsLoading ? `is-loading` : ''}`}
          >
            <div>
              <Question
                sponsoredType={sponsored}
                question={question}
                onAnswerClick={handleAnswerClick}
                onAnswerLinkClick={onAnswerLinkClick}
                onAnswerDropdownClick={onAnswerDropdownClick}
                pollResults={pollResults}
                totalQuestions={totalQuestions}
                totalPollSubmissions={totalPollSubmissions}
                resultImage={resultImage}
                socialShareComponent={socialShareComponent}
                textAnswered={resultText}
                poll={poll}
              />
              {resultActive() && puckAfterVoteContent && (
                <div className="poll-item-after-vote">
                  {puckAfterVoteContent}
                </div>
              )}
              {totalQuestions > 1 && showNextButton() && (
                <div className={`question-next-button-container`}>
                  <button
                    disabled={!showNextButton()}
                    onClick={localNextQuestionHandler}
                    className={`question-next-button ${
                      showNextButton() ? `is--active` : `is--inactive`
                    }`}
                    aria-label={t('common:next')}
                    style={{
                      '--poll-next-question-button-bg-color':
                        pollColors.nextQuestionButtonBgColor,
                      '--poll-next-question-button-text-color':
                        pollColors.nextQuestionButtonTextColor,
                      '--poll-next-question-button-bg-hover-color':
                        pollColors.nextQuestionButtonBgHoverColor,
                    }}
                  >
                    {t('poll:nextQuestion')}
                    <PollArrow />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {poll?.puckProductPanelContent
          ? poll.puckProductPanelContent
          : !!products?.length && (
              <PollProducts
                title={productsTitle}
                products={products}
                question={question}
                onAnswerDropdownClick={onAnswerDropdownClick}
                onAnswerLinkClick={onAnswerLinkClick}
                poll={poll}
              />
            )}

        {slotContent && <div className="poll-item-slot">{slotContent}</div>}
        {!(resultActive() && question.index === totalQuestions - 1) && skipButton}
        {nextStepButton}
      </div>
      {puckAfterCardContent}
    </div>
    </PuckPollContext.Provider>
  )
}

export default PollItem
